// Wrapper de fetch contra el backend Picking.
//
// - baseURL relativa `/api/*` — Vite proxy en dev, mismo origen en prod.
// - `credentials: 'include'` para que la cookie HttpOnly viaje en cada request.
// - 401 → emite evento global `auth:expired` que el router escucha y manda a /login,
//   sin contaminar cada caller con redirects.
// - Errores se lanzan como `ApiError` con `.status` y `.detail` ya extraídos
//   del JSON del backend (`{detail: "..."}`), o el mensaje genérico si no hay JSON.

const BASE = '/api';

export class ApiError extends Error {
  constructor(message, { status = 0, detail = null } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
}

async function request(method, path, { body, headers = {}, signal } = {}) {
  const init = {
    method,
    credentials: 'include',
    headers: { Accept: 'application/json', ...headers },
    signal,
  };
  if (body !== undefined) {
    init.headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }

  let res;
  try {
    res = await fetch(BASE + path, init);
  } catch (e) {
    // Red caída (typeError "Failed to fetch") o el browser está offline.
    throw new ApiError('Sin conexión con el servidor', { status: 0, detail: e.message });
  }

  // 401 → globalmente: notificamos y lanzamos para que la vista pare.
  if (res.status === 401) {
    window.dispatchEvent(new CustomEvent('auth:expired'));
    throw new ApiError('Sesión caducada', { status: 401 });
  }

  // No-content
  if (res.status === 204) return null;

  // Intentar parsear JSON aunque sea un error — el backend siempre devuelve JSON.
  let data = null;
  const text = await res.text();
  if (text) {
    try { data = JSON.parse(text); }
    catch { /* respuesta no-JSON (raro) — la metemos como detail crudo */ data = text; }
  }

  if (!res.ok) {
    const detail = data && typeof data === 'object' && 'detail' in data ? data.detail : data;
    const msg = typeof detail === 'string' ? detail : `HTTP ${res.status}`;
    throw new ApiError(msg, { status: res.status, detail });
  }

  return data;
}

export const apiGet   = (path, opts) => request('GET',    path, opts);
export const apiPost  = (path, body, opts) => request('POST',  path, { ...opts, body });
export const apiPut   = (path, body, opts) => request('PUT',   path, { ...opts, body });
export const apiPatch = (path, body, opts) => request('PATCH', path, { ...opts, body });
export const apiDel   = (path, opts) => request('DELETE', path, opts);
